// import api's and mutations
import { getRosterCycleEndResult } from 'RestAPIs/cost-centers/roster-cycle';

// import components
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import PlanningCycleDialogContent from './PlanningCycleDialogContent';
import { PlanningCycleDialogStyles } from './PlanningCycle.styles';

// import hooks
import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import useSaveRosterStateSettings from 'components/roster-portal/plan-units/Settings/hooks/useSaveRosterStateSettings';
// import styles
import { StyledPlanningCycleDialog } from './PlanningCycleDialog.styles';

// import types
import type { FC } from 'react';
import type {
  PlanningCycle,
  PlanningCycleDialogProps,
  PlanningCycleRound,
  POSTBody,
  RecurrenceRuleType,
  RosterCycleEndResult
} from './PlanningCycle.types';

// import utils
import { getRecurrenceRule } from '../RecurrenceRule/RecurrenceRule.util';
import dayjs from 'dayjs';

const PlanningCycleDialog: FC<PlanningCycleDialogProps> = ({
  open = true,
  onHidden,
  type,
  currentPlanningCycle = null,
  costCenterId
}) => {
  'use memo';

  const { t } = useTranslation('common');
  const [recurrenceRule, setRecurrenceRule] =
    useState<RecurrenceRuleType | null>(
      getRecurrenceRule(currentPlanningCycle)
    );

  const [cycleWindow, setCycleWindow] = useState(
    currentPlanningCycle?.cycleWindow || 1
  );
  const [rounds, setRounds] = useState(currentPlanningCycle?.rounds || []);
  const [rosterCycles, setRosterCycles] = useState<
    RosterCycleEndResult[] | null
  >(null);
  const { mutate: saveRosterSettings } = useSaveRosterStateSettings();

  // Deliberately kept as an explicit useMemo: a debounced function only works
  // if the same instance survives across renders, so this memo is load-bearing
  // rather than an optimisation the compiler can take over.
  const getRosterCycles = useMemo(
    () =>
      debounce((planningCycle: PlanningCycle, signal: AbortSignal) => {
        const body: POSTBody = {
          rosterState: type,
          enabled: true,
          recurrenceRule: {
            start: dayjs(planningCycle?.start)
              .startOf('day')
              .toISOString(),
            periodType: planningCycle?.periodType,
            interval: planningCycle?.interval,
            dayInWeekSet: planningCycle?.dayInWeekSet,
            dayOfMonth: planningCycle?.dayOfMonth,
            setPosition: planningCycle?.setPosition
          },
          cycleWindow: planningCycle?.cycleWindow,
          rounds: planningCycle?.rounds
        };

        // Cancelling only stops a request that has not been sent yet. The
        // signal covers the rest: an answer that is already on its way may no
        // longer land in state once something newer has superseded it.
        getRosterCycleEndResult(body, costCenterId).then(({ data }) => {
          if (!signal.aborted) {
            setRosterCycles(data);
          }
        });
      }, 500),
    [setRosterCycles, costCenterId, type]
  );

  const handleSave = () => {
    if (
      !recurrenceRule ||
      !recurrenceRule.start ||
      !recurrenceRule.periodType ||
      !recurrenceRule.interval
    )
      return;

    const planningCyclePayload: POSTBody = {
      rosterState: type,
      enabled: true,
      recurrenceRule: {
        start: recurrenceRule.start,
        periodType: recurrenceRule.periodType!,
        interval: recurrenceRule.interval!,
        dayInWeekSet: recurrenceRule?.dayInWeekSet,
        setPosition: recurrenceRule?.setPosition,
        dayOfMonth: recurrenceRule?.dayOfMonth
      },
      cycleWindow: cycleWindow,
      rounds: rounds.map((round) => ({
        startIndex: round.startIndex,
        endIndex: round.endIndex,
        autoConnect: round.autoConnect
      }))
    };

    saveRosterSettings(
      { costCenterId, planningCyclePayload },
      {
        onSuccess: () => {
          onHidden(true);
        },
        onError: (error) => {
          console.error('Failed to save planning cycle:', error);
        }
      }
    );
  };

  const addRound = () => {
    const lastRound = rounds.length > 0 ? rounds[rounds.length - 1] : undefined;
    const start = (lastRound?.endIndex ?? 0) + 1;
    const newRound = {
      startIndex: start,
      endIndex: start + 6,
      autoConnect: false
    };
    setRounds([...rounds, newRound]);
  };

  const updateRound = (index: number, round: PlanningCycleRound) => {
    setRounds([...rounds.slice(0, index), round, ...rounds.slice(index + 1)]);
  };

  const deleteRound = (index) => {
    setRounds(rounds.filter((item, idx) => idx !== index));
  };

  useEffect(() => {
    const request = new AbortController();

    if (recurrenceRule && open) {
      const newPlanningCycle: PlanningCycle = {
        start: recurrenceRule?.start,
        periodType: recurrenceRule?.periodType,
        interval: recurrenceRule?.interval,
        dayInWeekSet: recurrenceRule?.dayInWeekSet,
        dayOfMonth: recurrenceRule?.dayOfMonth,
        setPosition: recurrenceRule?.setPosition || 1,

        cycleWindow,
        rounds,
        rosterState: type,
        recurrenceRule: recurrenceRule
      };
      getRosterCycles(newPlanningCycle, request.signal);
    } else {
      setRosterCycles(null);
    }

    // Closing the dialog or unmounting must not leave an armed debounce behind
    // that fires half a second later and repopulates the examples, and anything
    // already on its way is stale from here on.
    return () => {
      getRosterCycles.cancel();
      request.abort();
    };
  }, [recurrenceRule, cycleWindow, rounds, getRosterCycles, open, type]);

  // sets the initial values for the dialog
  useEffect(() => {
    setRecurrenceRule(getRecurrenceRule(currentPlanningCycle) || null);
    setCycleWindow(currentPlanningCycle?.cycleWindow || 1);
    setRounds(currentPlanningCycle?.rounds || []);
  }, [currentPlanningCycle]);

  return (
    <StyledPlanningCycleDialog
      title={t('recurrenceDialog.title')}
      open={open}
      aria-labelledby="recurrence-dialog"
      maxWidth="large"
      onHidden={() => onHidden(false)}
      handleClose={() => onHidden(false)}
    >
      <PlanningCycleDialogStyles>
        <PlanningCycleDialogContent
          type={type}
          setRecurrenceRule={setRecurrenceRule}
          recurrenceRule={recurrenceRule}
          cycleWindow={cycleWindow}
          setCycleWindow={setCycleWindow}
          rounds={rounds}
          updateRound={updateRound}
          deleteRound={deleteRound}
          addRound={addRound}
          rosterCycles={rosterCycles}
        />
      </PlanningCycleDialogStyles>

      <DialogActions>
        <Button
          onClick={() => onHidden(false)}
          color="secondary"
          variant="contained"
        >
          {t('cancel')}
        </Button>
        <Button
          disabled={!recurrenceRule}
          onClick={handleSave}
          color="primary"
          variant="contained"
        >
          {t('save')}
        </Button>
      </DialogActions>
    </StyledPlanningCycleDialog>
  );
};

export default PlanningCycleDialog;
